import {
	BehaviorSubject,
	combineLatest as observableCombineLatest,
	concat,
	EMPTY,
	Observable,
	of as observableOf,
	ReplaySubject,
	timer
} from "rxjs";

import {
	catchError,
	distinctUntilChanged,
	filter,
	map,
	mergeMap,
	shareReplay,
	switchMap,
	takeWhile
} from "rxjs/operators";
import { Injectable } from "@angular/core";

import { Account, Destructable, Trade, UnauthorizedError, User, UserVerificationState, Wallet } from "app/models/";

import { Helper, parseList } from "@helpers/functions";
import { AccountService } from "../../account/services/account.service";
import { FiatWalletService } from "./wallet/fiat_wallet.service";
import { TradableWalletListService } from "./wallet/tradable_wallet_list.service";
import { WalletService } from "./wallet/wallet.service";
import { FiatService } from "./currency";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { convertTradeToTransaction } from "@helpers/tradable-convert";
import {
	CryptoTransaction,
	DerivativeTransaction,
	FiatTransaction,
	GenericTransaction,
	IndexTransaction,
	MetalTransaction,
	TradableTransaction,
	Transaction
} from "@models/transaction";
import { InitAssetsService } from "@core-services/init-assets.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { SiftScienceService } from "@core-services/sift-science.service";
import { DestructorService } from "@core-services/destructor.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { CoreStoreFacade } from "@modules/+new/core/core-store/domain/+state/core-store.facade";
import { LeveragedTokenTransaction } from "@models/transaction/leveraged_token_transaction";
import { AppropriatenessFacade } from "@modules/main/components/stocks/appropriateness-modal/+state/appropriateness.facade";
import { UserMembershipResponse } from "@main-services/user-membership.service";
import { SecurityTokenTransaction } from "@models/transaction/security_token_transaction";
import { UK_COUNTRY_ID } from "@helpers/constants";

@Injectable({
	providedIn: "root"
})
export class UserService implements Destructable {
	public name: string = ServiceNames.USER;

	public unverified = false;

	private _redirect: string = null;

	public _user: User = new User();
	public _triedSession = false;

	private _activity: BehaviorSubject<Activity> = new BehaviorSubject(new Activity());

	public _userLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject(false);
	private block: boolean = false;

	private POLL_INTERVAL_MS = 60000;

	private readonly user$$: ReplaySubject<User | null> = new ReplaySubject<User | null>(1);
	private readonly initialUserSession$$: ReplaySubject<User> = new ReplaySubject<User>(1);
	private readonly userPoll$: Observable<User> = timer(0, this.POLL_INTERVAL_MS).pipe(
		mergeMap(() =>
			this.coreStoreFacade.hasAccessToken()
				? this.apicallService.get("user/session", new ApicallOption(User, this._user))
				: EMPTY
		),
		map((users) => users[0] as User),
		shareReplay({
			bufferSize: 1,
			refCount: true
		})
	);

	private readonly user$: Observable<User> = concat(this.initialUserSession$$, this.userPoll$).pipe(
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	public readonly verificationState$ = this.user$.pipe(
		map((user) => user.getVerified()),
		distinctUntilChanged(),
		takeWhile((verificationState) => verificationState !== UserVerificationState.VERIFIED, true)
	);

	public readonly has2fa$: Observable<boolean> = this.user$$.pipe(map((user) => user.get2FA()));

	public constructor(
		private apicallService: ApicallService,
		private accountService: AccountService,
		private translationService: TranslationService,
		private masterService: MasterdataService,
		private fiatWalletService: FiatWalletService,
		private siftScienceService: SiftScienceService,
		private destructor: DestructorService,
		private fiatService: FiatService,
		private tradableWalletListService: TradableWalletListService,
		private initService: InitService,
		private initAssetsService: InitAssetsService,
		private readonly coreStoreFacade: CoreStoreFacade,
		private readonly appropriatenessFacade: AppropriatenessFacade
	) {
		this.destructor.register(this);

		this.walletServices.forEach((s) => s.getTransactionUpdateReceived().subscribe(() => this.getActivities()));

		this.accountService.accountUpdated.subscribe((account) => {
			if (account && account.isDocumented()) {
				this.user.setVerified(UserVerificationState.DOCUMENTED);
			}
			if (!!account && !!account.getTradingAppropriateness()) {
				this.user.setTradingAppropriateness(account.getTradingAppropriateness());
			}
		});
	}

	private initAccount(): Observable<boolean> {
		return this.apicallService.get("user/settings", new ApicallOption(Account)).pipe(
			map(
				(account) => {
					this.accountService.account = account[0];
					this.initService.init(this.name);
					return true;
				},
				(e) => Helper.handleError(e)
			),
			catchError((error) => {
				Helper.handleError(error);
				return [];
			})
		);
	}

	public noActivity(): boolean {
		return this._activity.getValue().empty();
	}

	public get isAtLeastDocumented(): boolean {
		return this.user.isDocumented() || this.user.isVerified();
	}

	public get isUnverified(): boolean {
		return this.user.isUnverified();
	}

	public get isVerified(): boolean {
		return this.user.isVerified();
	}

	public get isVerificationMandatory(): boolean {
		return this.user.isUnverified() && this.masterService.isAmlActive;
	}

	public isVerificationPending(): boolean {
		return this.user.isVerificationPending();
	}

	public get hasIgnoredVerify(): boolean {
		return this.user.hasIgnoredVerify();
	}

	public getActivities(): void {
		if (this.block || !this.loggedIn) {
			return;
		}
		this.block = true;
		const activity = new Activity();
		this.apicallService.get("activities").subscribe(
			// TODO: eslint fix
			/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
			(res: any) => {
				const attributes = res.attributes;
				if (this.coreStoreFacade.coreStoreSnapshot.isOffline) {
					return;
				}

				// FIAT Data
				activity.fiat_wallet_transactions = parseList(attributes.fiat_wallet_transactions, FiatTransaction);
				activity.fiat_wallet_transactions.forEach((f) => f.setAmounts());
				activity.pending_fiat_wallet_transactions = parseList(
					attributes.pending_fiat_wallet_transactions,
					FiatTransaction
				);
				activity.pending_fiat_wallet_transactions.forEach((f) => f.setAmounts());

				// FIAT Earn Data
				activity.fiat_earn_wallet_transactions = parseList(
					attributes.fiat_earn_wallet_transactions,
					DerivativeTransaction
				);
				activity.fiat_earn_pending_wallet_transactions = parseList(
					attributes.fiat_earn_pending_wallet_transactions,
					DerivativeTransaction
				);
				parseList(attributes.fiat_earn_trades, Trade).forEach((trade: Trade) =>
					activity.fiat_earn_trades.push(
						convertTradeToTransaction(
							trade,
							new DerivativeTransaction(),
							this.fiatService.getItemById(trade.getFiatId())
						)
					)
				);

				// Crypto Data
				activity.cryptocoin_wallet_transactions = parseList(
					attributes.cryptocoin_wallet_transactions,
					CryptoTransaction
				);
				activity.cryptocoin_pending_wallet_transactions = parseList(
					attributes.cryptocoin_pending_wallet_transactions,
					CryptoTransaction
				);
				parseList(attributes.cryptocoin_trades, Trade).forEach((trade: Trade) =>
					activity.cryptocoin_trades.push(
						convertTradeToTransaction(
							trade,
							new CryptoTransaction(),
							this.fiatService.getItemById(trade.getFiatId())
						)
					)
				);

				// Metals Data
				activity.commodity_wallet_transactions = parseList(
					attributes.commodity_wallet_transactions,
					MetalTransaction
				);
				activity.commodity_pending_wallet_transactions = parseList(
					attributes.commodity_pending_wallet_transactions,
					MetalTransaction
				);
				parseList(attributes.commodity_trades, Trade).forEach((trade: Trade) =>
					activity.commodity_trades.push(
						convertTradeToTransaction(
							trade,
							new MetalTransaction(),
							this.fiatService.getItemById(trade.getFiatId())
						)
					)
				);

				// Indices
				activity.user_index_actions = parseList(attributes.user_index_actions, IndexTransaction);

				// Stocks Data
				activity.stock_wallet_transactions = parseList(
					attributes.stock_wallet_transactions,
					DerivativeTransaction
				);
				activity.stock_pending_wallet_transactions = parseList(
					attributes.stock_pending_wallet_transactions,
					DerivativeTransaction
				);
				parseList(attributes.stock_trades, Trade).forEach((trade: Trade) =>
					activity.stock_trades.push(
						convertTradeToTransaction(
							trade,
							new DerivativeTransaction(),
							this.fiatService.getItemById(trade.getFiatId())
						)
					)
				);

				// ETFs Data
				activity.etf_wallet_transactions = parseList(attributes.etf_wallet_transactions, DerivativeTransaction);
				activity.etf_pending_wallet_transactions = parseList(
					attributes.etf_pending_wallet_transactions,
					DerivativeTransaction
				);
				parseList(attributes.etf_trades, Trade).forEach((trade: Trade) =>
					activity.etf_trades.push(
						convertTradeToTransaction(
							trade,
							new DerivativeTransaction(),
							this.fiatService.getItemById(trade.getFiatId())
						)
					)
				);

				// ETCs Data
				activity.etc_wallet_transactions = parseList(attributes.etc_wallet_transactions, DerivativeTransaction);
				activity.etc_pending_wallet_transactions = parseList(
					attributes.etc_pending_wallet_transactions,
					DerivativeTransaction
				);
				parseList(attributes.etc_trades, Trade).forEach((trade: Trade) =>
					activity.etc_trades.push(
						convertTradeToTransaction(
							trade,
							new DerivativeTransaction(),
							this.fiatService.getItemById(trade.getFiatId())
						)
					)
				);

				// L-Tokens Data
				if (attributes.leveraged_token_wallet_transactions) {
					activity.leveraged_token_wallet_transactions = parseList(
						attributes.leveraged_token_wallet_transactions,
						LeveragedTokenTransaction
					);
				}

				if (attributes.leveraged_token_pending_wallet_transactions) {
					activity.leveraged_token_pending_wallet_transactions = parseList(
						attributes.leveraged_token_pending_wallet_transactions,
						LeveragedTokenTransaction
					);
				}

				if (attributes.leveraged_token_trades) {
					parseList(attributes.leveraged_token_trades, Trade).forEach((trade: Trade) =>
						activity.leveraged_token_trades.push(
							convertTradeToTransaction(
								trade,
								new LeveragedTokenTransaction(),
								this.fiatService.getItemById(trade.getFiatId())
							)
						)
					);
				}

				// Security Tokens Data
				if (attributes.security_token_wallet_transactions) {
					activity.security_token_wallet_transactions = parseList(
						attributes.security_token_wallet_transactions,
						SecurityTokenTransaction
					);
				}
				if (attributes.security_token_pending_wallet_transactions) {
					activity.security_token_pending_wallet_transactions = parseList(
						attributes.security_token_pending_wallet_transactions,
						SecurityTokenTransaction
					);
				}
				if (attributes.security_token_trades) {
					parseList(attributes.security_token_trades, Trade).forEach((trade: Trade) =>
						activity.security_token_trades.push(
							convertTradeToTransaction(
								trade,
								new SecurityTokenTransaction(),
								this.fiatService.getItemById(trade.getFiatId())
							)
						)
					);
				}

				this._activity.next(activity);
				this.block = false;
			},
			(error) => Helper.handleError(error)
		);
	}

	public getSession(): Observable<boolean> {
		const option = new ApicallOption(User, this.user);
		option.ignore401 = true;

		if (!this._triedSession && this.coreStoreFacade.hasAccessToken()) {
			this._triedSession = true;
			return this.apicallService.get("user/session", option).pipe(
				mergeMap((users: User[]) => {
					const user = users[0];
					this._user = user;
					Helper.setCookie("user-currency", user.getUserCurrency());
					this.initialUserSession$$.next(user);
					this.initialUserSession$$.complete();
					this.user$$.next(user);
					this.appropriatenessFacade.setAppropriatenessState(user.getTradingAppropriateness());
					return this.postLogin();
				}),
				catchError((error) => {
					const isUnauthorizedError = error instanceof UnauthorizedError;
					if (!isUnauthorizedError) {
						// We don't want to show the error toast for UnauthorizedError
						Helper.handleError(error);
					}
					this.coreStoreFacade.clearAccessToken();
					return observableOf(false);
				}),
				shareReplay({ refCount: true, bufferSize: 1 })
			);
		}
		return observableOf(false);
	}

	public get loggedIn(): boolean {
		return this.user.getLoggedIn();
	}

	public destroy(): void {
		this._user = new User();
	}

	public get user(): User {
		return this._user;
	}

	public userLoggedIn(): BehaviorSubject<boolean> {
		return this._userLoggedIn;
	}

	private postLogin(): Observable<boolean> {
		this.walletServices.forEach((service: WalletService<Wallet, Transaction>) => service.socketInit());
		this.accountService.socketInit();
		this._user.setLoggin(true);
		this.siftScienceService.onLogin(this.coreStoreFacade.getAccessToken());
		this.user.set2fa_code("");
		const lang: string = this.user.getLanguage();
		this.translationService.setLanguage(lang);
		Helper.setCookie("pid", this.user.getId());
		this._userLoggedIn.next(true);

		return TranslationService.LanguagesObservable().pipe(
			filter((languages: string[]) => languages.length !== 0),
			switchMap(() =>
				observableCombineLatest([
					this.masterService.setMasterData(lang),
					this.initAssetsService.initialize(),
					this.initAccount()
				]).pipe(map((a: boolean[]) => a.every((a: boolean) => a)))
			)
		);
	}

	public activity(): Observable<Activity> {
		return this._activity.asObservable();
	}

	public set redirect(s: string) {
		this._redirect = s;
	}

	public get redirect(): string {
		return this._redirect;
	}

	private get walletServices(): WalletService<Wallet, Transaction>[] {
		return [...this.tradableWalletListService.services, this.fiatWalletService];
	}

	public userStatusUpdate(user: User): void {
		return this.user$$.next(user);
	}

	public getMembershipData$(): Observable<UserMembershipResponse> {
		return this.apicallService.get("user/membership-state");
	}

	public isWaitingForDocuments(): boolean {
		return this.user.isBusinessUser() && this.user.getAccountStatus() === "WAITING_FOR_DOCUMENTS";
	}

	public isUkResident(): boolean {
		return this._user.getCountryId() === UK_COUNTRY_ID;
	}
}

class Activity {
	// Crypto
	public cryptocoin_wallet_transactions: GenericTransaction[] = [];
	public cryptocoin_trades: TradableTransaction[] = [];
	public cryptocoin_pending_wallet_transactions: CryptoTransaction[] = [];
	// Leverage Token
	public leveraged_token_wallet_transactions: LeveragedTokenTransaction[] = [];
	public leveraged_token_trades: TradableTransaction[] = [];
	public leveraged_token_pending_wallet_transactions: LeveragedTokenTransaction[] = [];
	// Metals
	public commodity_wallet_transactions: MetalTransaction[] = [];
	public commodity_trades: TradableTransaction[] = [];
	public commodity_pending_wallet_transactions: MetalTransaction[] = [];
	// Fiat
	public fiat_wallet_transactions: FiatTransaction[] = [];
	public pending_fiat_wallet_transactions: FiatTransaction[] = [];

	// Fiat Earn
	public fiat_earn_wallet_transactions: DerivativeTransaction[] = [];
	public fiat_earn_pending_wallet_transactions: DerivativeTransaction[] = [];
	public fiat_earn_trades: DerivativeTransaction[] = [];

	// Index
	public user_index_actions: IndexTransaction[] = [];
	// Stocks
	public stock_wallet_transactions: DerivativeTransaction[] = [];
	public stock_trades: TradableTransaction[] = [];
	public stock_pending_wallet_transactions: DerivativeTransaction[] = [];
	// Etfs
	public etf_wallet_transactions: DerivativeTransaction[] = [];
	public etf_trades: TradableTransaction[] = [];
	public etf_pending_wallet_transactions: DerivativeTransaction[] = [];
	// Etcs
	public etc_wallet_transactions: DerivativeTransaction[] = [];
	public etc_trades: TradableTransaction[] = [];
	public etc_pending_wallet_transactions: DerivativeTransaction[] = [];
	// Security Tokens
	public security_token_wallet_transactions: SecurityTokenTransaction[] = [];
	public security_token_trades: TradableTransaction[] = [];
	public security_token_pending_wallet_transactions: SecurityTokenTransaction[] = [];

	public empty(): boolean {
		return (
			this.cryptocoin_wallet_transactions.length === 0 &&
			this.cryptocoin_trades.length === 0 &&
			this.cryptocoin_pending_wallet_transactions.length === 0 &&
			this.leveraged_token_wallet_transactions.length === 0 &&
			this.leveraged_token_trades.length === 0 &&
			this.leveraged_token_pending_wallet_transactions.length === 0 &&
			this.commodity_trades.length === 0 &&
			this.commodity_pending_wallet_transactions.length === 0 &&
			this.commodity_wallet_transactions.length === 0 &&
			this.fiat_wallet_transactions.length === 0 &&
			this.pending_fiat_wallet_transactions.length === 0 &&
			this.user_index_actions.length === 0 &&
			this.stock_trades.length === 0 &&
			this.stock_pending_wallet_transactions.length === 0 &&
			this.stock_wallet_transactions.length === 0 &&
			this.etf_trades.length === 0 &&
			this.etf_pending_wallet_transactions.length === 0 &&
			this.etf_wallet_transactions.length === 0 &&
			this.etc_trades.length === 0 &&
			this.etc_pending_wallet_transactions.length === 0 &&
			this.etc_wallet_transactions.length === 0 &&
			this.fiat_earn_wallet_transactions.length === 0 &&
			this.fiat_earn_pending_wallet_transactions.length === 0 &&
			this.fiat_earn_trades.length === 0 &&
			this.security_token_wallet_transactions.length === 0 &&
			this.security_token_trades.length === 0 &&
			this.security_token_pending_wallet_transactions.length === 0
		);
	}
}
